import React from 'react';
import { Box, Typography, Select, MenuItem, Button, TextField } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Dayjs } from 'dayjs';
import { COLORS, TIME_PERIOD_OPTIONS } from '../../pages/app/reports/groups-teams-activity/constants';
import { TagOption, CustomDateRange } from '../../pages/app/reports/groups-teams-activity/types';
import { TagPicker } from './tag-picker';

interface FilterBarProps {
    timePeriod: string;
    onTimePeriodChange: (value: string) => void;
    customDateRange?: CustomDateRange;
    onCustomDateRangeChange?: (range: CustomDateRange) => void;
    tagOptions: TagOption[];
    selectedTags: string[];
    onTagsChange: (tags: string[]) => void;
    onApply: () => void;
    onClear: () => void;
    onExport?: () => void;
    tagLabel?: string;
    /** User tag picker (optional — shown when provided) */
    userTagOptions?: TagOption[];
    selectedUserTags?: string[];
    onUserTagsChange?: (tags: string[]) => void;
    /** Tags that have actually been applied (post-Apply click). Used for the active-filter banner. */
    appliedTags?: string[];
    appliedUserTags?: string[];
    /** Called when a user removes a single tag from the active-filter banner. */
    onRemoveAppliedTag?: (tag: string) => void;
    onRemoveAppliedUserTag?: (tag: string) => void;
}

const datePickerFieldSx = {
    '& .MuiInputBase-root': {
        fontSize: '13px',
        fontFamily: 'Poppins, sans-serif',
        borderRadius: '6px',
        height: '36px',
    },
    '& .MuiInputBase-input': {
        padding: '6px 0 6px 10px',
        fontSize: '13px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: COLORS.border,
    },
    '& .MuiInputLabel-root': {
        fontSize: '13px',
        fontFamily: 'Poppins, sans-serif',
    },
    width: '150px',
};

const labelSx = {
    fontSize: '10px',
    color: COLORS.grayMedium,
    fontWeight: 500,
    textTransform: 'uppercase' as const,
    letterSpacing: '.5px',
};

export const FilterBar: React.FC<FilterBarProps> = ({
    timePeriod,
    onTimePeriodChange,
    customDateRange,
    onCustomDateRangeChange,
    tagOptions,
    selectedTags,
    onTagsChange,
    onApply,
    onClear,
    onExport,
    tagLabel = 'Team Tags',
    userTagOptions,
    selectedUserTags = [],
    onUserTagsChange,
    appliedTags = [],
    appliedUserTags = [],
    onRemoveAppliedTag,
    onRemoveAppliedUserTag,
}) => {
    const isCustom = timePeriod === 'custom';
    const hasActiveFilters = appliedTags.length > 0 || appliedUserTags.length > 0;

    const handleStartDateChange = (date: Dayjs | null) => {
        if (onCustomDateRangeChange) {
            onCustomDateRangeChange({
                startDate: date,
                endDate: customDateRange?.endDate ?? null,
            });
        }
    };

    const handleEndDateChange = (date: Dayjs | null) => {
        if (onCustomDateRangeChange) {
            onCustomDateRangeChange({
                startDate: customDateRange?.startDate ?? null,
                endDate: date,
            });
        }
    };

    return (
        <Box sx={{ marginBottom: '16px' }}>
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: '10px',
                flexWrap: 'wrap',
            }}
        >
            {/* Time Period */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography sx={labelSx}>
                    Time Period
                </Typography>
                <Select
                    value={timePeriod}
                    onChange={(e) => onTimePeriodChange(e.target.value)}
                    size="small"
                    sx={{
                        minWidth: '150px',
                        fontSize: '13px',
                        fontFamily: 'Poppins, sans-serif',
                        borderRadius: '6px',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: COLORS.border,
                        },
                        '& .MuiSelect-select': {
                            padding: '6px 28px 6px 10px',
                        },
                    }}
                >
                    {TIME_PERIOD_OPTIONS.map((opt) => (
                        <MenuItem
                            key={opt.value}
                            value={opt.value}
                            sx={{
                                fontSize: '13px',
                                fontFamily: 'Poppins, sans-serif',
                                ...(opt.value === 'custom' ? {
                                    borderTop: `1px solid ${COLORS.border}`,
                                    marginTop: '4px',
                                    paddingTop: '8px',
                                } : {}),
                            }}
                        >
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            {/* Custom Date Range Pickers */}
            {isCustom && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography sx={labelSx}>
                            Start Date
                        </Typography>
                        <DatePicker
                            value={customDateRange?.startDate ?? null}
                            onChange={handleStartDateChange}
                            maxDate={customDateRange?.endDate ?? undefined}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    placeholder="MM/DD/YYYY"
                                    sx={datePickerFieldSx}
                                />
                            )}
                        />
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        paddingBottom: '8px',
                    }}>
                        <Typography sx={{ fontSize: '13px', color: COLORS.grayMedium }}>
                            to
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography sx={labelSx}>
                            End Date
                        </Typography>
                        <DatePicker
                            value={customDateRange?.endDate ?? null}
                            onChange={handleEndDateChange}
                            minDate={customDateRange?.startDate ?? undefined}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    placeholder="MM/DD/YYYY"
                                    sx={datePickerFieldSx}
                                />
                            )}
                        />
                    </Box>
                </LocalizationProvider>
            )}

            {/* Team Tags */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography sx={labelSx}>
                    {tagLabel}
                </Typography>
                <TagPicker
                    options={tagOptions}
                    selected={selectedTags}
                    onChange={onTagsChange}
                />
            </Box>

            {/* User Tags */}
            {userTagOptions && onUserTagsChange && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Typography sx={labelSx}>
                        User Tags
                    </Typography>
                    <TagPicker
                        options={userTagOptions}
                        selected={selectedUserTags}
                        onChange={onUserTagsChange}
                        placeholder="User tags…"
                        colorVariant="amber"
                    />
                </Box>
            )}

            {/* Buttons */}
            <Button
                onClick={onApply}
                sx={{
                    padding: '6px 16px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 500,
                    backgroundColor: COLORS.primary,
                    color: '#fff',
                    textTransform: 'none',
                    fontFamily: 'Poppins, sans-serif',
                    '&:hover': { backgroundColor: '#2b5a8a' },
                }}
            >
                Apply
            </Button>
            <Typography
                onClick={onClear}
                sx={{
                    padding: '6px 10px',
                    fontSize: '13px',
                    cursor: 'pointer',
                    color: COLORS.grayDark,
                    '&:hover': { color: COLORS.textPrimary },
                }}
            >
                Clear
            </Typography>

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />

            {/* Export */}
            <Typography
                onClick={onExport}
                sx={{
                    fontSize: '12px',
                    color: onExport ? COLORS.primary : COLORS.grayMedium,
                    cursor: onExport ? 'pointer' : 'default',
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    '&:hover': onExport ? { textDecoration: 'underline' } : {},
                }}
            >
                <FileDownloadOutlinedIcon sx={{ fontSize: '14px' }} />
                Export
            </Typography>
        </Box>

        {/* Active Filter Banner */}
        {hasActiveFilters && (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '8px',
                    padding: '6px 12px',
                    backgroundColor: '#EFF6FF',
                    borderRadius: '6px',
                    border: `1px solid ${COLORS.blueLight}`,
                }}
            >
                <Typography sx={{ fontSize: '11px', color: COLORS.grayDark, fontWeight: 500, whiteSpace: 'nowrap' }}>
                    Filtered by:
                </Typography>
                {appliedTags.map((tag) => (
                    <Box
                        key={`team-${tag}`}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            padding: '2px 6px 2px 8px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: 500,
                            backgroundColor: COLORS.blueLight,
                            color: COLORS.blueDark,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {tag}
                        <Box
                            onClick={() => onRemoveAppliedTag?.(tag)}
                            sx={{
                                width: '14px',
                                height: '14px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: '#BFDBFE' },
                            }}
                        >
                            <CloseIcon sx={{ fontSize: '10px' }} />
                        </Box>
                    </Box>
                ))}
                {appliedUserTags.map((tag) => (
                    <Box
                        key={`user-${tag}`}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            padding: '2px 6px 2px 8px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: 500,
                            backgroundColor: COLORS.amberLight,
                            color: COLORS.amberDark,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {tag}
                        <Box
                            onClick={() => onRemoveAppliedUserTag?.(tag)}
                            sx={{
                                width: '14px',
                                height: '14px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: COLORS.amberHover },
                            }}
                        >
                            <CloseIcon sx={{ fontSize: '10px' }} />
                        </Box>
                    </Box>
                ))}
                <Typography
                    onClick={onClear}
                    sx={{
                        fontSize: '11px',
                        color: COLORS.primary,
                        cursor: 'pointer',
                        fontWeight: 500,
                        marginLeft: 'auto',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    Clear filters
                </Typography>
            </Box>
        )}
        </Box>
    );
};
